import type { TemplatesReadInstanceValuesOutput } from '@vivport/backend/types';
import { AppLink } from '@/components/ui/app-link';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useTemplateInstanceValues } from '@/hooks/useTemplateQueries';
import { toContentHref } from '@/utils/content-ref';
import { toolErrorText } from '@/utils/tool-error';
import { csvFileSlug, downloadCsv, toCsv } from './csv';
import { Download } from 'lucide-react';

interface TemplateResultsTabProps {
  templateId: number;
  templateName: string;
}

const VALUES_FIXED_HEADERS = ['Title', 'Updated'];

// A colliding label/value key arrives qualified as '<sectionSlug>.<key>'; bare keys have no dot.
function displayFieldKey(key: string): string {
  const dot = key.indexOf('.');
  if (dot === -1) return key;
  const section = key.slice(0, dot).replace(/-/g, ' ');
  return `${section.charAt(0).toUpperCase()}${section.slice(1)} · ${key.slice(dot + 1)}`;
}
const ENTRIES_FIXED_HEADERS = ['Instance', 'Row'];

function valuesRows(data: TemplatesReadInstanceValuesOutput): string[][] {
  return data.instances.map((instance) => [
    instance.title ?? instance.code,
    new Date(instance.updatedAt).toLocaleDateString(),
    ...data.fieldKeys.map((key) => instance.values[key] ?? ''),
  ]);
}

function entriesRows(data: TemplatesReadInstanceValuesOutput): string[][] {
  return data.entries.map((entry) => [
    entry.instanceCode,
    String(entry.rowNumber),
    ...data.columnHeaders.map((header) => entry.cells[header] ?? ''),
    entry.photo ? 'photo' : '',
  ]);
}

const ENTRY_RECORDS_FIXED_HEADERS = ['Instance', 'Title', 'Category'];

type EntrySection = TemplatesReadInstanceValuesOutput['entrySections'][number];

function entryRecordHeaders(section: EntrySection): string[] {
  return [...ENTRY_RECORDS_FIXED_HEADERS, ...section.def.fields.map((field) => field.label), 'Photos', 'Notes'];
}

function entryRecordRows(data: TemplatesReadInstanceValuesOutput, section: EntrySection): string[][] {
  return data.entryRecords
    .filter((record) => record.sectionKey === section.sectionKey)
    .map((record) => [
      record.instanceCode,
      record.title,
      record.category ?? '',
      ...section.def.fields.map((field) => record.values[field.key] ?? ''),
      String(record.photoCount),
      record.notes ?? '',
    ]);
}

function ResultsTable({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left">
          <tr>
            {headers.map((header) => (
              <th key={header} className="whitespace-nowrap px-3 py-2 font-medium">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="px-3 py-2 align-top">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TemplateResultsTab({ templateId, templateName }: TemplateResultsTabProps) {
  const query = useTemplateInstanceValues(templateId);

  if (query.isError) {
    return <p className="text-sm text-destructive">{toolErrorText(query.error)}</p>;
  }
  if (query.data === undefined) {
    return <p className="text-sm text-muted-foreground">Loading results…</p>;
  }
  const data = query.data;
  if (data.instances.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No instances yet — results appear here once the template has been used.
      </p>
    );
  }

  const slug = csvFileSlug(templateName);
  const valuesHeaders = [...VALUES_FIXED_HEADERS, ...data.fieldKeys.map(displayFieldKey)];
  const entriesHeaders = [...ENTRIES_FIXED_HEADERS, ...data.columnHeaders, 'Photo'];
  const warningCount = data.instances.reduce((sum, instance) => sum + instance.warnings.length, 0);
  const warningLines = data.instances.flatMap((instance) =>
    instance.warnings.map((warning) => `${instance.title ?? instance.code}: ${warning}`)
  );

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium">Values</h3>
            <span className="text-xs text-muted-foreground">
              {data.instances.length} of {data.instancesTotal} instance{data.instancesTotal === 1 ? '' : 's'}
            </span>
            {warningCount > 0 ? (
              <Badge variant="outline" title={warningLines.join('\n')}>
                {warningCount} warning{warningCount === 1 ? '' : 's'}
              </Badge>
            ) : null}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadCsv(`${slug}-values.csv`, toCsv(valuesHeaders, valuesRows(data)))}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Download CSV
          </Button>
        </div>
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                {valuesHeaders.map((header) => (
                  <th key={header} className="whitespace-nowrap px-3 py-2 font-medium">
                    {header}
                  </th>
                ))}
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.instances.map((instance) => (
                <tr key={instance.instanceId}>
                  <td className="px-3 py-2 align-top">
                    <AppLink to={toContentHref(instance.code)} className="text-primary hover:underline">
                      {instance.title ?? instance.code}
                    </AppLink>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 align-top">
                    {new Date(instance.updatedAt).toLocaleDateString()}
                  </td>
                  {data.fieldKeys.map((key) => (
                    <td key={key} className="px-3 py-2 align-top">
                      {instance.values[key] ?? ''}
                    </td>
                  ))}
                  <td className="px-3 py-2 align-top">
                    {instance.written ? <Badge variant="secondary">Written</Badge> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium">Table rows</h3>
            <span className="text-xs text-muted-foreground">
              {data.entriesTruncated
                ? `showing ${data.entries.length} of ${data.entriesTotal} rows`
                : `${data.entries.length} row${data.entries.length === 1 ? '' : 's'}`}
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={data.entries.length === 0}
            onClick={() => downloadCsv(`${slug}-entries.csv`, toCsv(entriesHeaders, entriesRows(data)))}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            Download CSV
          </Button>
        </div>
        {data.entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">No table rows yet.</p>
        ) : (
          <ResultsTable headers={entriesHeaders} rows={entriesRows(data)} />
        )}
      </section>

      {data.entrySections.map((section) => {
        const headers = entryRecordHeaders(section);
        const rows = entryRecordRows(data, section);
        return (
          <section key={section.sectionKey} className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-medium">Entries · {section.sectionTitle || section.sectionKey}</h3>
                <span className="text-xs text-muted-foreground">
                  {data.entryRecordsTruncated
                    ? `showing ${rows.length} of ${data.entryRecordsTotal} records across entry sections`
                    : `${rows.length} record${rows.length === 1 ? '' : 's'}`}
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={rows.length === 0}
                onClick={() => downloadCsv(`${slug}-${section.sectionKey}.csv`, toCsv(headers, rows))}
              >
                <Download className="mr-1.5 h-3.5 w-3.5" />
                Download CSV
              </Button>
            </div>
            {rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">No entries yet.</p>
            ) : (
              <ResultsTable headers={headers} rows={rows} />
            )}
          </section>
        );
      })}
    </div>
  );
}
